import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DietaryPreferencesData } from "@/components/DietaryPreferences";
import { Json } from "@/integrations/supabase/types";

interface UseUserProfileResult {
  isLoading: boolean;
  dietaryPreferences: DietaryPreferencesData;
  healthGoals: string[];
  customGoal: string | null;
  saveDietaryPreferences: (preferences: DietaryPreferencesData) => Promise<void>;
  saveHealthGoals: (goals: string[], customGoal?: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const DEFAULT_PREFERENCES: DietaryPreferencesData = { lifestyles: [], allergens: [] };

// Helper to safely cast Json to DietaryPreferencesData
const parseDietaryPreferences = (json: Json | null): DietaryPreferencesData | null => {
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  const obj = json as Record<string, unknown>;
  if (Array.isArray(obj.lifestyles) && Array.isArray(obj.allergens)) {
    return {
      lifestyles: obj.lifestyles as string[],
      allergens: obj.allergens as string[],
    };
  }
  return null;
};

export const useUserProfile = (userId?: string, isGuest?: boolean): UseUserProfileResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [dietaryPreferences, setDietaryPreferences] = useState<DietaryPreferencesData>(DEFAULT_PREFERENCES);
  const [healthGoals, setHealthGoals] = useState<string[]>([]);
  const [customGoal, setCustomGoal] = useState<string | null>(null);

  // Load profile from database for authenticated non-guest users
  const loadProfile = useCallback(async () => {
    if (!userId || isGuest) {
      // For guests, load from localStorage
      const savedDietary = localStorage.getItem("metabolic_dietary");
      const savedGoals = localStorage.getItem("metabolic_goals");
      const savedCustomGoal = localStorage.getItem("metabolic_custom_goal");
      
      if (savedDietary) setDietaryPreferences(JSON.parse(savedDietary));
      if (savedGoals) setHealthGoals(JSON.parse(savedGoals));
      if (savedCustomGoal) setCustomGoal(savedCustomGoal);
      
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("dietary_preferences, health_goals, custom_goal")
        .eq("user_id", userId)
        .single();

      if (error) {
        console.error("Error loading profile:", error);
        // Fall back to localStorage
        const savedDietary = localStorage.getItem("metabolic_dietary");
        const savedGoals = localStorage.getItem("metabolic_goals");
        const savedCustomGoal = localStorage.getItem("metabolic_custom_goal");
        
        if (savedDietary) setDietaryPreferences(JSON.parse(savedDietary));
        if (savedGoals) setHealthGoals(JSON.parse(savedGoals));
        if (savedCustomGoal) setCustomGoal(savedCustomGoal);
      } else if (data) {
        // Load from database
        const dbPreferences = parseDietaryPreferences(data.dietary_preferences);
        const dbGoals = data.health_goals as string[] | null;
        const dbCustomGoal = data.custom_goal as string | null;

        // Use database values, or fall back to localStorage for migration
        if (dbPreferences && (dbPreferences.lifestyles?.length > 0 || dbPreferences.allergens?.length > 0)) {
          setDietaryPreferences(dbPreferences);
        } else {
          const savedDietary = localStorage.getItem("metabolic_dietary");
          if (savedDietary) {
            const localPrefs = JSON.parse(savedDietary);
            setDietaryPreferences(localPrefs);
            // Migrate to database - done after hook returns
          }
        }

        if (dbGoals && dbGoals.length > 0) {
          setHealthGoals(dbGoals);
        } else {
          const savedGoals = localStorage.getItem("metabolic_goals");
          if (savedGoals) {
            const localGoals = JSON.parse(savedGoals);
            setHealthGoals(localGoals);
          }
        }

        if (dbCustomGoal) {
          setCustomGoal(dbCustomGoal);
        } else {
          const savedCustomGoal = localStorage.getItem("metabolic_custom_goal");
          if (savedCustomGoal) {
            setCustomGoal(savedCustomGoal);
          }
        }
      }
    } catch (err) {
      console.error("Error loading profile:", err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isGuest]);

  // Save to database helper
  const saveToDB = async (updates: { dietary_preferences?: Json; health_goals?: string[]; custom_goal?: string | null }) => {
    if (!userId || isGuest) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("user_id", userId);

      if (error) {
        console.error("Error saving to profile:", error);
      }
    } catch (err) {
      console.error("Error saving to profile:", err);
    }
  };

  // Save dietary preferences
  const saveDietaryPreferences = async (preferences: DietaryPreferencesData) => {
    setDietaryPreferences(preferences);
    
    // Always save to localStorage as backup
    localStorage.setItem("metabolic_dietary", JSON.stringify(preferences));
    
    // Save to database for authenticated users
    if (userId && !isGuest) {
      await saveToDB({ dietary_preferences: preferences as unknown as Json });
    }
  };

  // Save health goals
  const saveHealthGoals = async (goals: string[], customGoalText?: string) => {
    setHealthGoals(goals);
    setCustomGoal(customGoalText || null);
    
    // Always save to localStorage as backup
    localStorage.setItem("metabolic_goals", JSON.stringify(goals));
    if (customGoalText) {
      localStorage.setItem("metabolic_custom_goal", customGoalText);
    } else {
      localStorage.removeItem("metabolic_custom_goal");
    }
    
    // Save to database for authenticated users
    if (userId && !isGuest) {
      await saveToDB({ 
        health_goals: goals,
        custom_goal: customGoalText || null
      });
    }
  };

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return {
    isLoading,
    dietaryPreferences,
    healthGoals,
    customGoal,
    saveDietaryPreferences,
    saveHealthGoals,
    refreshProfile: loadProfile,
  };
};
